import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Filter, Grid, List } from 'lucide-react';
import { Event } from '@/lib/index';
import { EVENTS_DATA } from '@/data/events';
import { EventCard } from '@/components/Cards';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const CATEGORIES: Event['category'][] = [
  'Sport',
  'Littérature',
  'Solidarité',
  'Atelier',
  'Spectacle',
  'Cérémonies',
  'Don du sang',
  'Sorties',
];

const CATEGORY_COLORS: Record<Event['category'], string> = {
  Sport: 'bg-accent/10 text-accent border-accent/20',
  Littérature: 'bg-primary/10 text-primary border-primary/20',
  Solidarité: 'bg-destructive/10 text-destructive border-destructive/20',
  'Don du sang': 'bg-destructive/10 text-destructive border-destructive/20',
  Atelier: 'bg-secondary/50 text-secondary-foreground border-secondary',
  Sorties: 'bg-muted text-muted-foreground border-border',
  Spectacle: 'bg-primary/10 text-primary border-primary/20',
  Cérémonies: 'bg-muted text-muted-foreground border-border',
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
  }).format(date);
}

function CalendarView({ events }: { events: Event[] }) {
  const eventsByMonth = useMemo(() => {
    const grouped: Record<string, Event[]> = {};
    events.forEach((event) => {
      const date = new Date(event.date);
      const monthKey = new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'long',
      }).format(date);
      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }
      grouped[monthKey].push(event);
    });
    return grouped;
  }, [events]);

  return (
    <div className="space-y-12">
      {Object.entries(eventsByMonth).map(([month, monthEvents]) => (
        <motion.div
          key={month}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h3 className="text-2xl font-semibold mb-6 capitalize text-foreground">
            {month}
          </h3>
          <div className="space-y-4">
            {monthEvents.map((event) => (
              <motion.div
                key={event.id}
                className="flex gap-4 p-4 rounded-xl bg-card border border-border hover:shadow-lg transition-all duration-200"
                whileHover={{ scale: 1.01 }}
              >
                <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-primary/10 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold text-primary">
                    {new Date(event.date).getDate()}
                  </span>
                  <span className="text-xs text-muted-foreground uppercase">
                    {new Intl.DateTimeFormat('fr-FR', { month: 'short' }).format(
                      new Date(event.date)
                    )}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h4 className="font-semibold text-lg text-foreground">
                      {event.title}
                    </h4>
                    <Badge
                      variant="outline"
                      className={CATEGORY_COLORS[event.category]}
                    >
                      {event.category}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {event.description}
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {event.time && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{event.time}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{event.location}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

export default function Agenda() {
  const [selectedCategories, setSelectedCategories] = useState<Event['category'][]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'calendar'>('grid');

  const filteredEvents = useMemo(() => {
    let filtered = [...EVENTS_DATA];

    if (selectedCategories.length > 0) {
      filtered = filtered.filter((event) =>
        selectedCategories.includes(event.category)
      );
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (event) =>
          event.title.toLowerCase().includes(query) ||
          event.description.toLowerCase().includes(query) ||
          event.location.toLowerCase().includes(query)
      );
    }

    return filtered.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [selectedCategories, searchQuery]);

  const toggleCategory = (category: Event['category']) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="relative bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-8 h-8 text-primary" />
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                Agenda
              </h1>
            </div>
            <p className="text-lg text-muted-foreground">
              Découvrez tous les événements à venir à Gréasque. Spectacles,
              ateliers, cérémonies et activités pour tous.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          <motion.aside
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:w-64 flex-shrink-0"
          >
            <div className="sticky top-24 space-y-6">
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-foreground">Filtres</h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Rechercher
                    </label>
                    <Input
                      type="text"
                      placeholder="Rechercher un événement..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-3 block">
                      Catégories
                    </label>
                    <div className="space-y-2">
                      {CATEGORIES.map((category) => (
                        <button
                          key={category}
                          onClick={() => toggleCategory(category)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                            selectedCategories.includes(category)
                              ? 'bg-primary text-primary-foreground font-medium'
                              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                          }`}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  </div>

                  {selectedCategories.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedCategories([])}
                      className="w-full"
                    >
                      Réinitialiser les filtres
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.aside>

          <motion.main
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex-1"
          >
            <div className="flex items-center justify-between mb-8">
              <p className="text-muted-foreground">
                <span className="font-semibold text-foreground">
                  {filteredEvents.length}
                </span>{' '}
                événement{filteredEvents.length > 1 ? 's' : ''} trouvé{filteredEvents.length > 1 ? 's' : ''}
              </p>

              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('calendar')}
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {filteredEvents.length === 0 ? (
              <div className="text-center py-16">
                <Calendar className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  Aucun événement trouvé
                </h3>
                <p className="text-muted-foreground">
                  Essayez de modifier vos filtres de recherche
                </p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredEvents.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <EventCard event={event} />
                  </motion.div>
                ))}
              </div>
            ) : (
              <CalendarView events={filteredEvents} />
            )}
          </motion.main>
        </div>
      </div>
    </div>
  );
}
